import { isHlsSupported } from '@lib/hls/support';
import { type FrameCaptureOptions, getVideoFrame } from '@lib/media/hlsFrameCapture';
import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import Hls from 'hls.js';

/** Message thrown when HLS.js is not supported (e.g. older iOS). Use for UI detection. */
export const THUMBNAIL_UNSUPPORTED_ERROR =
  'Thumbnail generation is not supported on this device. HLS.js requires MSE (e.g. iOS 17.1+).';

/** User-facing message shown when thumbnails are not supported. */
export const THUMBNAIL_UNSUPPORTED_MESSAGE =
  'Thumbnails are not supported. Please upgrade your OS.';

/**
 * Thumbnails for a moment at a given timestamp.
 *
 * This is a thin adapter over `@lib/media/hlsFrameCapture`, which owns the
 * hidden player, the serial queue and the byte-bounded frame cache. It used to
 * own all three itself, building and destroying a whole Hls instance per frame
 * and holding every generated base64 data URL for the rest of the session.
 *
 * Frames now come back as object URLs over Blobs whose lifetime the capture
 * module owns — so treat what `getThumbnail` returns as borrowed. Read it again
 * when you need it rather than parking it somewhere long-lived; see
 * `useMomentThumbnail` for the pattern.
 *
 * Nothing here is observable any more, and there is nothing to dispose: the
 * capture player's lifetime is leased by the surfaces that display frames, not
 * by this app-wide provider.
 */
export class MomentThumbnailStore extends ReactionManager {
  async getThumbnail(
    libraryId: string,
    mediaId: string,
    timestamp: number,
    options?: FrameCaptureOptions,
    signal?: AbortSignal,
  ): Promise<string> {
    // Only block when both app (mock) and browser say unsupported; when mock is on but browser has MSE, allow so timeline loads.
    if (!isHlsSupported() && !Hls.isSupported()) {
      throw new Error(THUMBNAIL_UNSUPPORTED_ERROR);
    }

    try {
      return await getVideoFrame(libraryId, mediaId, timestamp, options, signal);
    } catch (error) {
      // `MomentThumbnail` distinguishes the "upgrade your OS" state by comparing
      // the message string, so the capture layer's own wording can't reach it.
      if (error instanceof Error && error.name === 'FrameCaptureUnsupportedError') {
        throw new Error(THUMBNAIL_UNSUPPORTED_ERROR);
      }
      throw error;
    }
  }
}

export const { Provider: MomentThumbnailProvider, useStore: useMomentThumbnailStore } =
  createContext(MomentThumbnailStore, {
    dispose: (store) => store.deinit(),
  });
